import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import type { ZakatGoldInput, CalendarType } from '@/types/zakat'
import { Coins } from 'lucide-react'

interface ZakatGoldFormProps {
  input: ZakatGoldInput
  calendar: CalendarType
  onInputChange: (input: ZakatGoldInput) => void
  onCalendarChange: (calendar: CalendarType) => void
  onCalculate: () => void
}

export default function ZakatGoldForm({
  input,
  calendar,
  onInputChange,
  onCalendarChange,
  onCalculate,
}: ZakatGoldFormProps) {
  const { t } = useTranslation()
  const handleInputChange = (field: keyof ZakatGoldInput, value: string) => {
    const numValue = parseFloat(value) || 0
    onInputChange({
      ...input,
      [field]: numValue,
    })
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-6">
        <Coins className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('zakat.gold.title')}</h3>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t('zakat.yearOfHawl')}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="hijri"
              checked={calendar === 'hijri'}
              onChange={() => onCalendarChange('hijri')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.hijri')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="gregorian"
              checked={calendar === 'gregorian'}
              onChange={() => onCalendarChange('gregorian')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.gregorian')}</span>
          </label>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            label={t('zakat.gold.grams18')}
            value={input.grams18.toString()}
            onChange={(e) => handleInputChange('grams18', e.target.value)}
            placeholder={t('zakat.placeholderGrams')}
          />
          <Input
            type="number"
            label={t('zakat.gold.price18')}
            value={input.price18.toString()}
            onChange={(e) => handleInputChange('price18', e.target.value)}
            placeholder={t('zakat.placeholderPrice')}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            label={t('zakat.gold.grams21')}
            value={input.grams21.toString()}
            onChange={(e) => handleInputChange('grams21', e.target.value)}
            placeholder={t('zakat.placeholderGrams')}
          />
          <Input
            type="number"
            label={t('zakat.gold.price21')}
            value={input.price21.toString()}
            onChange={(e) => handleInputChange('price21', e.target.value)}
            placeholder={t('zakat.placeholderPrice')}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            label={t('zakat.gold.grams24')}
            value={input.grams24.toString()}
            onChange={(e) => handleInputChange('grams24', e.target.value)}
            placeholder={t('zakat.placeholderGrams')}
          />
          <Input
            type="number"
            label={t('zakat.gold.price24')}
            value={input.price24.toString()}
            onChange={(e) => handleInputChange('price24', e.target.value)}
            placeholder={t('zakat.placeholderPrice')}
          />
        </div>
      </div>

      <a
        href="https://dahabmasr.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm mb-4 block"
      >
        {t('zakat.knowGoldPrice')}
      </a>

      <Button variant="primary" size="lg" className="w-full" onClick={onCalculate}>
        {t('zakat.calculateYourZakat')}
      </Button>
    </Card>
  )
}

